import { useSearchParams } from 'react-router-dom';

import { getShellInfo, useShellPresence } from '../../../services/nativeBridge/shellPresence';
import { PlatformEnum } from '../../../services/platformContext/platformContextTypes';

export const NATIVE_PLATFORMS = ['ios', 'android'] as const;

export type NativePlatform = (typeof NATIVE_PLATFORMS)[number];

// The shell already sends `?platform=APP_IOS|APP_ANDROID|WEB` for every other webview URL
// (PlatformContextService, usePaymentsUrl) — reuse that vocabulary instead of a second one, which
// would collide: PlatformContextService.getCorrectPlatform falls back to WEB on an unrecognized
// value, so a shell sending its usual `platform=APP_IOS` would silently read as WEB there while a
// hypothetical `platform=ios` would silently read as not-native here.
const PLATFORM_PARAM_TO_NATIVE: Partial<Record<string, NativePlatform>> = {
  [PlatformEnum.APP_IOS]: 'ios',
  [PlatformEnum.APP_ANDROID]: 'android',
};

const NATIVE_TO_PLATFORM_PARAM: Record<NativePlatform, PlatformEnum> = {
  ios: PlatformEnum.APP_IOS,
  android: PlatformEnum.APP_ANDROID,
};

/** The `?platform=` value to send when building a URL back to `/native`, e.g. on redirect. */
export const toPlatformParam = (platform: NativePlatform): PlatformEnum => NATIVE_TO_PLATFORM_PARAM[platform];

/**
 * Session-scoped so the platform survives navigation. The native shell puts `?platform=` on the URL
 * it loads, but the app routinely navigates to URLs without it — `navigate('/')` on logout, in
 * `useGoHome`, in the game and bingo headers — and a URL-only read turns a native visitor back into
 * a web one the first time that happens, dropping them on the web landing page.
 *
 * `sessionStorage` rather than a module variable alone, because a full reload (which some flows do)
 * would otherwise lose it just as thoroughly.
 */
const STORAGE_KEY = 'crowncoins.nativePlatform';

let remembered: NativePlatform | null = null;

const isNativePlatform = (value: string | null): value is NativePlatform =>
  NATIVE_PLATFORMS.includes(value as NativePlatform);

const readStored = (): NativePlatform | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);

    return isNativePlatform(stored) ? stored : null;
  } catch {
    // Storage can be unavailable (private mode, disabled site data); the in-memory value still works
    // for the life of the document.
    return null;
  }
};

const remember = (platform: NativePlatform): void => {
  remembered = platform;

  try {
    sessionStorage.setItem(STORAGE_KEY, platform);
  } catch {
    // Non-fatal — see readStored.
  }
};

/**
 * The platform declared in the URL, or null when it carries none. A strict read of this navigation:
 * use `getNativePlatform` or `useNativePlatform` to ask whether this visitor is native at all.
 */
export const readNativePlatformParam = (searchParams: URLSearchParams): NativePlatform | null => {
  const platformParam = searchParams.get('platform');

  return platformParam ? (PLATFORM_PARAM_TO_NATIVE[platformParam] ?? null) : null;
};

/**
 * Records the platform the native shell declared. Called once at boot, alongside
 * `platformContextService.initFromURL`, because that is the one moment the shell's URL is guaranteed
 * to be the current one.
 */
export const initNativePlatformFromURL = (search: string = window.location.search): void => {
  const fromUrl = readNativePlatformParam(new URLSearchParams(search));

  if (fromUrl) {
    remember(fromUrl);
  }
};

/** The platform for this session: whatever the shell declared, remembered across navigation. */
export const getNativePlatform = (): NativePlatform | null => remembered ?? readStored();

/**
 * The native shell tells us which store build it is, so the page doesn't have to infer it from the
 * webview user agent. Null for an ordinary web visitor.
 *
 * Only once the shell has answered `app.getInfo` (see `shellPresence`), like `rips-ui`'s `isInShell`:
 * the `?platform=` param, its session copy, or a bridge global that never answers must not turn a
 * plain browser tab into the native lander, whose provider buttons only work through the shell. The
 * platform comes from that reply, falling back to the param for a shell whose reply lacks it.
 */
export function useNativePlatform(): NativePlatform | null {
  const searchParams = useSearchParams()[0];
  const presence = useShellPresence();

  if (presence !== 'confirmed') {
    return null;
  }

  const fromShell = getShellInfo()?.platform;

  return (
    (isNativePlatform(fromShell ?? null) ? fromShell : null) ??
    readNativePlatformParam(searchParams) ??
    getNativePlatform()
  );
}
